import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { socket } from '../api/socket';
import { Radio, Sparkles, Film, X, ExternalLink } from 'lucide-react';
import { isRTL } from '../utils/translations';
import { useMarket } from '../context/MarketContext';

/**
 * Real-Time In-App Live Notification Banner
 * Dynamically notifies the client in real-time when:
 * 1. A Live Broadcast starts (livestream-started)
 * 2. A New Luxury Product is published (new-product)
 * 3. A New Video Reel is uploaded (new-video)
 * Appears seamlessly at the top of the browser screen without reloading.
 */
export default function RealtimeInAppBanner() {
    const [notification, setNotification] = useState(null);
    const [progress, setProgress] = useState(100);
    const navigate = useNavigate();
    const { country, language, currency } = useMarket();
    const rtl = isRTL(language);
    const timerRef = useRef(null);
    const progressIntervalRef = useRef(null);

    const triggerNotification = (notif) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);

        setNotification(notif);
        setProgress(100);

        const duration = notif.duration || 7000;
        const intervalStep = 50;
        const decrement = (intervalStep / duration) * 100;

        progressIntervalRef.current = setInterval(() => {
            setProgress((prev) => {
                const next = prev - decrement;
                if (next <= 0) {
                    clearInterval(progressIntervalRef.current);
                    return 0;
                }
                return next;
            });
        }, intervalStep);

        timerRef.current = setTimeout(() => {
            setNotification(null);
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        }, duration);
    };

    const handleDismiss = (e) => {
        if (e) e.stopPropagation();
        if (timerRef.current) clearTimeout(timerRef.current);
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        setNotification(null);
    };

    const handleNavigate = () => {
        if (!notification) return;
        const targetUrl = notification.url;
        handleDismiss();
        if (targetUrl) {
            navigate(targetUrl);
        }
    };

    useEffect(() => {
        if (!socket) return;

        // 1. Live stream broadcast started
        const handleLiveStarted = (data) => {
            try {
                const roomId = data.roomId || data.id;
                if (!roomId) return;
                const host = data.hostName || data.storeName || (rtl ? 'دار لوفونو' : 'LUXVONO Live');
                const title = data.title || (rtl ? 'بث مباشر حصري جديد' : 'New Exclusive Live Stream');
                const cover = data.coverImage || data.thumbnail || '';

                triggerNotification({
                    id: `live_${roomId}_${Date.now()}`,
                    type: 'LIVE',
                    tag: rtl ? '🔴 بث مباشر الآن' : '🔴 LIVE NOW',
                    title: host,
                    subtitle: title,
                    image: cover,
                    url: `/${country}/${language}/live/${roomId}`,
                    accentColor: '#FE2C55',
                    buttonText: rtl ? 'انضم للبث' : 'Join Live',
                    duration: 8500
                });
            } catch (err) {
                console.error('[RealtimeBanner] Error parsing live event:', err);
            }
        };

        // 2. New luxury product published
        const handleNewProduct = (data) => {
            try {
                const prodId = data.productId || data.id;
                if (!prodId) return;
                const name = data.name || data.title || (rtl ? 'قطعة فاخرة جديدة' : 'New Luxury Item');
                const price = data.price ? `${Number(data.price).toLocaleString()} ${currency || 'SAR'}` : (rtl ? 'وصل حديثاً' : 'New Arrival');
                let img = data.imageUrl || data.thumbnail || '';
                if (!img && Array.isArray(data.images) && data.images.length > 0) {
                    img = data.images[0];
                }

                triggerNotification({
                    id: `prod_${prodId}_${Date.now()}`,
                    type: 'PRODUCT',
                    tag: rtl ? '✨ قطعة فاخرة جديدة' : '✨ NEW ARRIVAL',
                    title: name,
                    subtitle: price,
                    image: img,
                    url: `/${country}/${language}/product/${prodId}`,
                    accentColor: '#D4AF37',
                    buttonText: rtl ? 'معاينة' : 'View',
                    duration: 7500
                });
            } catch (err) {
                console.error('[RealtimeBanner] Error parsing product event:', err);
            }
        };

        // 3. New video reel uploaded
        const handleNewVideo = (data) => {
            try {
                const videoId = data.videoId || data.id;
                const title = data.title || (rtl ? 'فيديو ريلز جديد' : 'New Video Reel');
                const creator = data.creatorName || data.storeName || (rtl ? 'استكشف التشكيلة الآن' : 'Explore Now');
                const cover = data.thumbnailUrl || data.coverUrl || '';

                triggerNotification({
                    id: `video_${videoId || Date.now()}`,
                    type: 'VIDEO',
                    tag: rtl ? '🎬 ريلز جديد' : '🎬 NEW REEL',
                    title: title,
                    subtitle: creator,
                    image: cover,
                    url: `/${country}/${language}/videos`,
                    accentColor: '#25F4EE',
                    buttonText: rtl ? 'مشاهدة' : 'Watch',
                    duration: 7000
                });
            } catch (err) {
                console.error('[RealtimeBanner] Error parsing video event:', err);
            }
        };

        socket.on('livestream-started', handleLiveStarted);
        socket.on('new-product', handleNewProduct);
        socket.on('new-video', handleNewVideo);

        return () => {
            socket.off('livestream-started', handleLiveStarted);
            socket.off('new-product', handleNewProduct);
            socket.off('new-video', handleNewVideo);
            if (timerRef.current) clearTimeout(timerRef.current);
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        };
    }, [country, language, currency, rtl]);

    if (!notification) return null;

    return (
        <div
            dir={rtl ? 'rtl' : 'ltr'}
            style={{
                position: 'fixed',
                top: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 999999,
                width: 'min(92vw, 480px)',
                animation: 'luxSlideDown 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards',
                cursor: 'pointer',
                fontFamily: 'inherit'
            }}
            onClick={handleNavigate}
        >
            <style>{`
                @keyframes luxSlideDown {
                    from {
                        opacity: 0;
                        transform: translate(-50%, -30px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translate(-50%, 0) scale(1);
                    }
                }
                @keyframes luxPulseGlow {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.2); opacity: 0.7; }
                }
            `}</style>

            <div
                style={{
                    background: 'rgba(15, 15, 19, 0.94)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    border: `1.5px solid ${notification.accentColor}55`,
                    boxShadow: `0 14px 40px rgba(0,0,0,0.65), 0 0 25px ${notification.accentColor}33`,
                    borderRadius: '18px',
                    overflow: 'hidden',
                    transition: 'all 0.25s ease'
                }}
            >
                <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Media Thumbnail / Icon */}
                    <div
                        style={{
                            width: '46px',
                            height: '46px',
                            borderRadius: '12px',
                            background: '#1c1c24',
                            border: `1px solid ${notification.accentColor}44`,
                            overflow: 'hidden',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                        }}
                    >
                        {notification.image ? (
                            <img
                                src={notification.image}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                        ) : null}

                        {!notification.image && notification.type === 'LIVE' && (
                            <Radio size={22} color={notification.accentColor} />
                        )}
                        {!notification.image && notification.type === 'PRODUCT' && (
                            <Sparkles size={22} color={notification.accentColor} />
                        )}
                        {!notification.image && notification.type === 'VIDEO' && (
                            <Film size={22} color={notification.accentColor} />
                        )}

                        {notification.type === 'LIVE' && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: '#FE2C55',
                                    boxShadow: '0 0 6px #FE2C55',
                                    animation: 'luxPulseGlow 1.2s infinite'
                                }}
                            />
                        )}
                    </div>

                    {/* Notification Copy */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                            <span
                                style={{
                                    fontSize: '11px',
                                    fontWeight: 800,
                                    color: notification.accentColor,
                                    letterSpacing: '0.3px'
                                }}
                            >
                                {notification.tag}
                            </span>
                            <span style={{ fontSize: '10px', color: '#666' }}>• {rtl ? 'الآن' : 'Just now'}</span>
                        </div>
                        <div
                            style={{
                                color: '#ffffff',
                                fontSize: '13.5px',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {notification.title}
                        </div>
                        <div
                            style={{
                                color: '#a0a0ab',
                                fontSize: '12px',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {notification.subtitle}
                        </div>
                    </div>

                    {/* Action Pill Button */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleNavigate();
                        }}
                        style={{
                            background: notification.accentColor,
                            color: notification.type === 'PRODUCT' ? '#000000' : '#ffffff',
                            border: 'none',
                            borderRadius: '10px',
                            padding: '7px 12px',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0,
                            transition: 'transform 0.15s ease',
                            boxShadow: `0 4px 12px ${notification.accentColor}44`
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                        <span>{notification.buttonText}</span>
                    </button>

                    {/* Close Cross */}
                    <button
                        onClick={handleDismiss}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#71717a',
                            padding: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            flexShrink: 0
                        }}
                        aria-label="Dismiss"
                    >
                        <X size={15} />
                    </button>
                </div>

                {/* Remaining Time Progress Bar */}
                <div style={{ height: '2px', background: '#22222a', width: '100%' }}>
                    <div
                        style={{
                            height: '100%',
                            width: `${progress}%`,
                            background: notification.accentColor,
                            transition: 'width 50ms linear'
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
